/**
 * Conveniences built on the port. These are deliberately free functions rather
 * than methods: every adapter would implement them identically, so there is no
 * reason to make each one write them again.
 */
import type { PutInput, RemoteEntry, RemoteId, RemoteStore } from './types';

/** "Is it still there?" — `stat` without the undefined check at every call site. */
export async function exists(store: RemoteStore, id: RemoteId): Promise<boolean> {
  const entry = await store.stat(id);
  return !!entry && !entry.trashed;
}

export interface PutJsonInput extends Omit<PutInput, 'blob' | 'mediaType'> {
  data: unknown;
}

/** Create or overwrite a JSON document. */
export function putJson(store: RemoteStore, input: PutJsonInput): Promise<RemoteEntry> {
  const { data, ...rest } = input;
  return store.put({ ...rest, blob: new Blob([JSON.stringify(data)], { type: 'application/json' }), mediaType: 'application/json' });
}

export async function getJson<T = unknown>(store: RemoteStore, id: RemoteId): Promise<T> {
  return JSON.parse(await (await store.get(id)).text()) as T;
}

export interface PutTextInput extends Omit<PutInput, 'blob'> {
  text: string;
  mediaType: string;
}

export function putText(store: RemoteStore, input: PutTextInput): Promise<RemoteEntry> {
  const { text, ...rest } = input;
  return store.put({ ...rest, blob: new Blob([text], { type: input.mediaType }) });
}

/**
 * The first name `taken` does not resolve, built by `candidate(n)` for
 * n = 1, 2, 3 … so each caller keeps its own naming style. `taken` is a lookup
 * rather than a store so callers with an injected `findByName` can pass theirs.
 *
 * Used where a file must never overwrite an older one (frozen monthly copies,
 * year-end finals): BFL 7 kap. keeps every version, so the app renames rather
 * than replaces.
 */
export async function unusedName(taken: (name: string) => Promise<unknown>, candidate: (n: number) => string): Promise<string> {
  for (let n = 1; ; n++) {
    const name = candidate(n);
    if (!(await taken(name))) return name;
  }
}
